import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../services/API";
// 1) GET ALL NOTIFICATIONS (supports type filter + pagination)
export const getAllNotification = createAsyncThunk("getAllNotification", async ({ type = null, page = 1 } = {}, { rejectWithValue }) => {
    try {
        let url = type ? `/notifications/?type=${type}&page=${page}` : `/notifications/?page=${page}`;
        const response = await axiosInstance.get(url);
        const data = response.data;
        const notifications = Array.isArray(data) ? data : (data.notifications || []);
        const hasMore = !Array.isArray(data) && !!data.next;
        return { notifications, hasMore, page };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});
// 2) GET LIKE NOTIFICATIONS
export const getLikeNotification = createAsyncThunk("getLikeNotification", async () => {
    try {
        const response = await axiosInstance.get(`/notifications/?type=post_like`);
        const result = response.data;
        return result;
    } catch (error) {
        return error.response.data;
    }
});
//3) GET COMMENT NOTIFICATIONS
export const getCommentNotification = createAsyncThunk("getCommentNotification", async () => {
    try {
        const response = await axiosInstance.get(`/notifications/?type=post_comment`);
        const result = response.data;
        return result;
    } catch (error) {
        return error.response.data;
    }
});
//4) GET FOLLOW NOTIFICATIONS
export const getFollowNotification = createAsyncThunk("getFollowNotification", async () => {
    try {
        const response = await axiosInstance.get(`/notifications/?type=user_follow`);
        const result = response.data;
        return result;
    } catch (error) {
        return error.response.data;
    }
});
//5) GET NOTIFICATION COUNT
export const notificationCount = createAsyncThunk("notificationCount", async (id) => {
    try {
        const response = await axiosInstance.get(`/notifications/notifications_count/`);
        const result = response.data;
        return result;
    } catch (error) {
        return error.response.data;
    }
});
//6) MARK NOTIFICATION AS READ
export const readNotification = createAsyncThunk("readNotification", async (id) => {
    try {
        const response = await axiosInstance.post(`/notifications/read/${id}/`);
        const result = response.data;
        return result;
    } catch (error) {
        return error.response.data;
    }
});
//7) ACCEPT FOLLOW REQUEST NOTIFICATION
export const acceptFollowRequestNotification = createAsyncThunk("acceptFollowRequestNotification", async (id) => {
    try {
        const response = await axiosInstance.post(`/notifications/follow-request/accept/${id}/`);
        const result = response.data;
        return result;
    } catch (error) {
        return error.response.data;
    }
});
//8) REJECT FOLLOW REQUEST NOTIFICATION/
export const rejectFollowRequestNotification = createAsyncThunk("rejectFollowRequestNotification", async (id) => {
    try {
        const response = await axiosInstance.post(`/notifications/follow-request/reject/${id}/`);
        const result = response.data;
        return result;
    } catch (error) {
        return error.response.data;
    }
});
//9) MARK ALL NOTIFICATIONS AS READ
export const readAllNotifications = createAsyncThunk("readAllNotifications", async () => {
    try {
        const response = await axiosInstance.post(`/notifications/all-as-read/`);
        const result = response.data;
        return result;
    } catch (error) {
        return error.response.data;
    }
});
//10) DELETE NOTIFICATION
export const deleteNotification = createAsyncThunk("deleteNotification", async (id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/notifications/delete_notification/${id}/`);
        const result = response.data;
        return { ...result, id }; // Include the original id in the response
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});
//11) DELETE ALL NOTIFICATIONS
export const deleteAllNotifications = createAsyncThunk("deleteAllNotifications", async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/notifications/delete_all_notifications/`);
        const result = response.data;
        return result;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});
//12) DELETE SELECTED NOTIFICATIONS
export const deleteSelectedNotifications = createAsyncThunk("deleteSelectedNotifications", async (ids, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(`/notifications/delete_selected_notifications/`, {
            data: { notification_ids: ids }
        });
        const result = response.data;
        return { ...result, ids }; // Include the original ids in the response
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});
//13) ACCEPT GROUP INVITE NOTIFICATION
export const acceptGroupInviteNotification = createAsyncThunk(
    "acceptGroupInviteNotification",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`/notifications/group-invites/${id}/accept/`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
//14) REJECT GROUP INVITE NOTIFICATION
export const rejectGroupInviteNotification = createAsyncThunk(
    "rejectGroupInviteNotification",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`/notifications/group-invites/${id}/reject/`, {});
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
export const NotificationSlice = createSlice({
    name: "Notification",
    initialState: {
        notificationData: { notifications: [] },
        notificationUnreadCount: 0,
        loading: false,
        loadingMore: false,
        hasMore: false,
        error: null,
    },
    extraReducers: (builder) => {
        // getAllNotification
        builder.addCase(getAllNotification.pending, (state, action) => {
            const page = action.meta.arg?.page ?? 1;
            if (page === 1) {
                state.loading = true;
            } else {
                state.loadingMore = true;
            }
            state.error = null;
        });
        builder.addCase(getAllNotification.fulfilled, (state, action) => {
            const { notifications, hasMore, page } = action.payload;
            state.loading = false;
            state.loadingMore = false;
            state.hasMore = hasMore;
            if (page === 1) {
                state.notificationData = { notifications };
            } else {
                state.notificationData = {
                    notifications: [...state.notificationData.notifications, ...notifications],
                };
            }
            state.error = null;
        });
        builder.addCase(getAllNotification.rejected, (state, action) => {
            state.loading = false;
            state.loadingMore = false;
            state.error = action.payload || action.error.message;
        });

        // getLikeNotification
        builder.addCase(getLikeNotification.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getLikeNotification.fulfilled, (state, action) => {
            state.loading = false;
            state.notificationData = { notifications: Array.isArray(action.payload.notifications) ? action.payload.notifications : (Array.isArray(action.payload) ? action.payload : []) };
        });
        builder.addCase(getLikeNotification.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // getCommentNotification
        builder.addCase(getCommentNotification.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getCommentNotification.fulfilled, (state, action) => {
            state.loading = false;
            state.notificationData = { notifications: Array.isArray(action.payload.notifications) ? action.payload.notifications : (Array.isArray(action.payload) ? action.payload : []) };
        });
        builder.addCase(getCommentNotification.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // getFollowNotification
        builder.addCase(getFollowNotification.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getFollowNotification.fulfilled, (state, action) => {
            state.loading = false;
            state.notificationData = { notifications: Array.isArray(action.payload.notifications) ? action.payload.notifications : (Array.isArray(action.payload) ? action.payload : []) };
        });
        builder.addCase(getFollowNotification.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // notificationCount
        builder.addCase(notificationCount.pending, (state) => {
            // state.loading = false;
        });
        builder.addCase(notificationCount.fulfilled, (state, action) => {
            // state.loading = false;
            state.notificationUnreadCount = action.payload;
        });
        builder.addCase(notificationCount.rejected, (state, action) => {
            // state.loading = false;
            state.error = action.payload;
        });

        // readNotification
        builder.addCase(readNotification.pending, (state) => {
            state.loading = false;
        });
        builder.addCase(readNotification.fulfilled, (state, action) => {
            state.loading = false;
            // No change to notificationData here, as it may not return the full list
        });
        builder.addCase(readNotification.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // acceptFollowRequestNotification
        builder.addCase(acceptFollowRequestNotification.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(acceptFollowRequestNotification.fulfilled, (state, action) => {
            state.loading = false;
            // No change to notificationData here, as it may not return the full list
        });
        builder.addCase(acceptFollowRequestNotification.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // rejectFollowRequestNotification
        builder.addCase(rejectFollowRequestNotification.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(rejectFollowRequestNotification.fulfilled, (state, action) => {
            state.loading = false;
            // No change to notificationData here, as it may not return the full list
        });
        builder.addCase(rejectFollowRequestNotification.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // readAllNotifications
        builder.addCase(readAllNotifications.pending, (state) => {
            // state.loading = true;
        });
        builder.addCase(readAllNotifications.fulfilled, (state, action) => {
            // state.loading = false;
            // No change to notificationData here, as it may not return the full list
        });
        builder.addCase(readAllNotifications.rejected, (state, action) => {
            // state.loading = false;
            state.error = action.payload;
        });        // deleteNotification
        builder.addCase(deleteNotification.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteNotification.fulfilled, (state, action) => {
            state.loading = false;
            const deletedId = action.payload.id;
            state.notificationData = {
                notifications: state.notificationData.notifications.filter(notification => notification.id !== deletedId)
            };
            state.error = null;
        });
        builder.addCase(deleteNotification.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error.message;
        });

        // deleteAllNotifications
        builder.addCase(deleteAllNotifications.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteAllNotifications.fulfilled, (state) => {
            state.loading = false;
            state.notificationData = { notifications: [] };
            state.error = null;
        });
        builder.addCase(deleteAllNotifications.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error.message;
        });

        // deleteSelectedNotifications
        builder.addCase(deleteSelectedNotifications.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteSelectedNotifications.fulfilled, (state, action) => {
            state.loading = false;
            // Use the ids from the payload to filter out deleted notifications
            const deletedIds = action.payload.ids || [];
            state.notificationData = {
                notifications: state.notificationData.notifications.filter(notification => !deletedIds.includes(notification.id))
            };
            state.error = null;
        });
        builder.addCase(deleteSelectedNotifications.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || action.error.message;
        });
    },
});

export default NotificationSlice.reducer;
